import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMembership } from '../contexts/MembershipContext';
import { membershipAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import { Clock, Lock, Ticket, CreditCard, Loader2 } from 'lucide-react';

export function UpgradeModal() {
    const navigate = useNavigate();
    const { showUpgradeModal, setShowUpgradeModal, dismissModal, refreshMembership, formatTimeRemaining, membership } = useMembership();
    const [accessCode, setAccessCode] = useState('');
    const [redeeming, setRedeeming] = useState(false);
    const [showCodeInput, setShowCodeInput] = useState(false);

    const handleRedeemCode = async () => {
        if (!accessCode.trim()) {
            toast.error('Please enter an access code');
            return;
        }

        setRedeeming(true);
        try {
            const response = await membershipAPI.redeemCode(accessCode.trim());
            toast.success(response.data.message);
            setShowUpgradeModal(false);
            setAccessCode('');
            setShowCodeInput(false);
            refreshMembership();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Invalid or expired access code');
        } finally {
            setRedeeming(false);
        }
    };

    const handlePayWithPayPal = () => {
        setShowUpgradeModal(false);
        // Delay navigation to let Dialog fully unmount and clean up body styles
        // (Radix Dialog adds pointer-events:none + overflow:hidden to body)
        setTimeout(() => navigate('/payment'), 200);
    };

    const isExpired = membership?.status === 'expired';

    return (
        <Dialog
            open={showUpgradeModal}
            onOpenChange={(open) => {
                if (open) setShowUpgradeModal(true);
                // Allow closing via dialog overlay only if not expired
                else if (!isExpired) setShowUpgradeModal(false);
            }}
        >
            <DialogContent
                className="max-w-md"
                onPointerDownOutside={(e) => { if (isExpired) e.preventDefault(); }}
                onEscapeKeyDown={(e) => { if (isExpired) e.preventDefault(); }}
                hideCloseButton={isExpired}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                        <Lock className="h-5 w-5 text-red-500" />
                        {isExpired ? 'Membership Expired' : 'Free Trial Expired'}
                    </DialogTitle>
                    <DialogDescription>
                        {isExpired
                            ? 'Your membership has expired. Upgrade to continue generating questions and accessing premium content.'
                            : 'Your 2-hour free trial has ended. Upgrade to continue studying.'
                        }
                    </DialogDescription>
                </DialogHeader>

                <div className="py-6 space-y-4">
                    {/* Time used display */}
                    <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-center">
                        <Clock className="h-8 w-8 mx-auto mb-2 text-red-500" />
                        <p className="text-sm text-red-700">
                            You've used all {formatTimeRemaining(membership?.time_used_seconds || 7200)} of your free trial
                        </p>
                    </div>

                    {/* Pricing options */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="p-4 rounded-lg border-2 border-indigo-200 bg-indigo-50 text-center">
                            <p className="text-2xl font-bold text-indigo-600">$5</p>
                            <p className="text-sm text-slate-600">Monthly</p>
                        </div>
                        <div className="p-4 rounded-lg border-2 border-emerald-200 bg-emerald-50 text-center">
                            <p className="text-2xl font-bold text-emerald-600">$50</p>
                            <p className="text-sm text-slate-600">Annual</p>
                            <p className="text-xs text-emerald-600 font-medium">Save $10!</p>
                        </div>
                    </div>

                    <Button
                        className="w-full bg-[#0070ba] hover:bg-[#005ea6] text-white"
                        onClick={handlePayWithPayPal}
                        data-testid="paypal-upgrade-btn"
                    >
                        <CreditCard className="mr-2 h-4 w-4" />
                        Pay with PayPal
                    </Button>

                    {/* Access code section */}
                    <div className="pt-4 border-t border-slate-200">
                        {!showCodeInput ? (
                            <Button
                                variant="outline"
                                className="w-full"
                                onClick={() => setShowCodeInput(true)}
                                data-testid="access-code-btn"
                            >
                                <Ticket className="mr-2 h-4 w-4" />
                                I have an access code
                            </Button>
                        ) : (
                            <div className="space-y-3">
                                <Label>Enter Access Code</Label>
                                <Input
                                    value={accessCode}
                                    onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                                    placeholder="XXXX-XXXX-XXXX"
                                    className="text-center tracking-widest uppercase"
                                    data-testid="access-code-input"
                                />
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        className="flex-1"
                                        onClick={() => { setShowCodeInput(false); setAccessCode(''); }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                                        onClick={handleRedeemCode}
                                        disabled={redeeming}
                                        data-testid="redeem-code-btn"
                                    >
                                        {redeeming ? (
                                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Redeeming...</>
                                        ) : 'Redeem Code'}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Maybe Later dismiss — only show when not in code-entry mode */}
                    {!showCodeInput && (
                        <button
                            onClick={dismissModal}
                            className="w-full text-xs text-slate-400 hover:text-slate-600 transition-colors py-1"
                            data-testid="dismiss-modal-btn"
                        >
                            Maybe later — browse without premium features
                        </button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export function MembershipBadge() {
    const { membership, formatTimeRemaining, isGracePeriod, isFreeTrial, isActive } = useMembership();
    const [timeLeft, setTimeLeft] = React.useState('');

    // Update timer every second for active memberships with less than 24 hours
    React.useEffect(() => {
        if (!isActive || !membership?.expires_at) return;

        const updateTimer = () => {
            const expiryDate = new Date(membership.expires_at);
            const now = new Date();
            const diffMs = expiryDate - now;
            
            if (diffMs <= 0) {
                setTimeLeft('Expired');
                return;
            }
            
            const diffSeconds = Math.floor(diffMs / 1000);
            const days = Math.floor(diffSeconds / 86400);
            const hours = Math.floor((diffSeconds % 86400) / 3600);
            const minutes = Math.floor((diffSeconds % 3600) / 60);
            const seconds = diffSeconds % 60;
            
            if (days > 0) {
                setTimeLeft(`${days}d ${hours}h left`);
            } else if (hours > 0) {
                setTimeLeft(`${hours}h ${minutes}m left`);
            } else if (minutes > 0) {
                setTimeLeft(`${minutes}m ${seconds}s left`);
            } else {
                setTimeLeft(`${seconds}s left`);
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [isActive, membership?.expires_at]);

    if (!membership) return null;

    // Active membership (from redeemed code or payment) - check FIRST before grace period
    if (isActive && membership.expires_at) {
        return (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium">
                <CreditCard className="h-3 w-3" />
                <span>Premium</span>
                <span className="opacity-60">({timeLeft})</span>
            </div>
        );
    }

    // Grace period - show countdown to launch (only if not active member)
    if (isGracePeriod && !isActive) {
        const launchDate = new Date(membership.grace_period_ends);
        const daysLeft = Math.ceil((launchDate - new Date()) / (1000 * 60 * 60 * 24));
        return (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium">
                <span>Full Access</span>
                <span className="opacity-60">({daysLeft} days left)</span>
            </div>
        );
    }

    // Free trial with time remaining
    if (isFreeTrial && membership.time_remaining_seconds > 0) {
        const timeStr = formatTimeRemaining(membership.time_remaining_seconds);
        const isLow = membership.time_remaining_seconds < 1800; // Less than 30 min
        return (
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
                isLow ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
            }`}>
                <Clock className="h-3 w-3" />
                <span>{timeStr} left</span>
            </div>
        );
    }

    return null;
}
